/**
 * Function to get walk observation filter options from Redux state if available, otherwise use defaults
 */
import { get } from "react-native/Libraries/TurboModule/TurboModuleRegistry";
import { FilterItem } from "../../components/LHFilter";
import { getPeriodOptions, getWeekOptions, getYearOptions } from "../../utils/filterOptions";
import { formatUserName } from "../../utils/emailTouserName";

/**
 * Helper function to transform employee filter options to show names instead of emails
 */
const transformEmployeeOptions = (options: { id: string; displayText: string }[]) => {
  return options.map(option => ({
    ...option,
    displayText: formatUserName(option.displayText) || option.displayText,
    email: option.displayText, // Store original email for API calls
  }));
};

/**
 * Helper function to find selectedOptionId for a filter
 */
const findSelectedOptionId = (
  filterKey: string,
  currentFilters: any,
  options: { id: string; displayText: string; email?: string }[]
): string | undefined => {
  const currentValue = currentFilters[filterKey];
  if (!currentValue || currentValue === "" || !options.length) return undefined;

  // For status, match by ID directly since the options now use the correct API values
  if (filterKey === "status") {
    return options.find(opt => opt.id === currentValue)?.id;
  }

  // For period and week, match by ID (API uses ID)
  if (filterKey === "period" || filterKey === "week") {
    return options.find(opt => opt.id === currentValue)?.id;
  }

  // For employee filters (createdBy, finalizedBy), try to match by email first, then displayText
  if (filterKey === "createdBy" || filterKey === "finalizedBy") {
    const matchedByEmail = options.find(opt => opt.email === currentValue);
    if (matchedByEmail) return matchedByEmail.id;
    
    // Fallback to displayText matching (for backward compatibility)
    return options.find(opt => opt.displayText === currentValue)?.id;
  }

  // For other filters, match by displayText
  return options.find(opt => opt.displayText === currentValue)?.id;
};

/**
 * Generates filter options for Walk Observations tab
 * @param filtersFromRedux Object containing filter options from Redux (keys: period, week, status, finalizedBy, createdBy, typeOfDamage)
 * @param employees Employee data from Redux state (for backward compatibility)
 * @param currentFilters Current filter values from Redux state
 * @returns Array of filter options with Redux data if available, otherwise defaults
 */
export const getWalkObservationFilterOptions = (
  filtersFromRedux?: {
    period?: { id: string; displayText: string }[];
    week?: { id: string; displayText: string }[];
    year?: { id: string; displayText: string }[];
    status?: { id: string; displayText: string }[];
    createdBy?: { id: string; displayText: string }[];
    finalizedBy?: { id: string; displayText: string }[];
    typeOfDamage?: { id: string; displayText: string }[];
  },
  employees?: any,
  currentFilters: any = {}
): FilterItem[] => {
  // Default options for each filter
  const defaultOptions = {
    period: [
      { id: "1", displayText: "P1" },
      { id: "2", displayText: "P2" },
      { id: "3", displayText: "P3" },
      { id: "4", displayText: "P4" },
      { id: "5", displayText: "P5" },
      { id: "6", displayText: "P6" },
      { id: "7", displayText: "P7" },
      { id: "8", displayText: "P8" },
      { id: "9", displayText: "P9" },
      { id: "10", displayText: "P10" },
      { id: "11", displayText: "P11" },
      { id: "12", displayText: "P12" },
    ],
    year: getYearOptions(),
    week: [
      { id: "1", displayText: "W1" },
      { id: "2", displayText: "W2" },
      { id: "3", displayText: "W3" },
      { id: "4", displayText: "W4" },
    ],
    status: [
      { id: "0", displayText: "In-Progress" },
      { id: "1", displayText: "Completed" },
    ],
    finalizedBy: [
      { id: "1", displayText: "Keerthi Velagala" },
      { id: "2", displayText: "Sohail Mohammed" },
      { id: "3", displayText: "Nuthi bhaskar" },
      { id: "4", displayText: "Vaibhav" },
    ],
    createdBy: [
      { id: "1", displayText: "Keerthi Velagala" },
      { id: "2", displayText: "Sohail Mohammed" },
      { id: "3", displayText: "Nuthi bhaskar" },
      { id: "4", displayText: "Vaibhav" },
    ],
    typeOfDamage: [
      { id: "1", displayText: "Bent Frame" },
      { id: "2", displayText: "Missing Bolt" },
      { id: "3", displayText: "Severe Impact" },
      { id: "4", displayText: "Other" },
    ],
  };

  // Always prioritize API filter data over default options and employee data
  let finalizedByOptions = defaultOptions.finalizedBy;
  let createdByOptions = defaultOptions.createdBy;

  // First check if we have filter options from the API
  if (
    filtersFromRedux?.finalizedBy &&
    filtersFromRedux.finalizedBy.length > 0
  ) {
    finalizedByOptions = transformEmployeeOptions(filtersFromRedux.finalizedBy);
  }

  if (filtersFromRedux?.createdBy && filtersFromRedux.createdBy.length > 0) {
    createdByOptions = transformEmployeeOptions(filtersFromRedux.createdBy);
  }

  // If no API data but we have employees, use them as fallback
  else if (employees && Array.isArray(employees?.data)) {
    const employeeOptions = employees.data
      .map((emp: any) => {
        const displayName = `${emp.First_Name || ""} ${
          emp.Last_Name || ""
        }`.trim();
        return {
          id: emp.GPID || emp.Id || emp.Email || "",
          displayText: displayName || emp.Email || emp.GPID || "Unknown",
          email: emp.Email || "", // Store email for API calls
        };
      })
      .filter((emp: any) => emp.id || emp.displayText !== "Unknown");

    if (employeeOptions.length > 0) {
      if (
        !filtersFromRedux?.finalizedBy ||
        filtersFromRedux.finalizedBy.length === 0
      ) {
        finalizedByOptions = employeeOptions;
      }
      if (
        !filtersFromRedux?.createdBy ||
        filtersFromRedux.createdBy.length === 0
      ) {
        createdByOptions = employeeOptions;
      }
    }
  }

  // Generate filter items with selectedOptionId
  const periodOptions = getPeriodOptions();
  const weekOptions = getWeekOptions();
  const yearOptions = getYearOptions();
  const statusOptions = filtersFromRedux?.status && filtersFromRedux.status.length > 0 ? filtersFromRedux.status : defaultOptions.status;
  const typeOfDamageOptions = filtersFromRedux?.typeOfDamage && filtersFromRedux.typeOfDamage.length > 0 ? filtersFromRedux.typeOfDamage : defaultOptions.typeOfDamage;

  return [
    {
      key: "period",
      label: "Period",
      options: periodOptions,
      selectedOptionId: findSelectedOptionId("period", currentFilters, periodOptions),
    },
    {
      key: "week",
      label: "Week",
      options: weekOptions,
      selectedOptionId: findSelectedOptionId("week", currentFilters, weekOptions),
    },
    {
      key: "year",
      label: "Year",
      options: yearOptions,
      selectedOptionId: findSelectedOptionId("year", currentFilters, yearOptions),
    },
    {
      key: "status",
      label: "Status",
      options: statusOptions,
      selectedOptionId: findSelectedOptionId("status", currentFilters, statusOptions),
    },
    {
      key: "created/by",
      label: "Created By",
      options: createdByOptions,
      selectedOptionId: findSelectedOptionId("createdBy", currentFilters, createdByOptions),
    },
    {
      key: "finalized/by",
      label: "Finalized By",
      options: finalizedByOptions,
      selectedOptionId: findSelectedOptionId("finalizedBy", currentFilters, finalizedByOptions),
    },
    {
      key: "type/of/damage",
      label: "Type of Damage",
      options: typeOfDamageOptions,
      selectedOptionId: findSelectedOptionId("typeOfDamage", currentFilters, typeOfDamageOptions),
    },
  ];
};
